import { Box, Grid, Input, Slider, Typography } from '@mui/material';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { LoggingContext } from '../Context/LoggingContext/LoggingContext';
import { UserContext } from '../Context/UserContext/UserContextProvider';
import { MeasureProps } from '../tools/generalProperties';

import CustomIcon from './CustomIcon';
import { colorDifference } from './GarmentResult/garmentResultFunctions';

export default function ComparisonSlider({
  eachMeasureProperties,
  eachMeasureKey,
  initialMeasure,
  idealMeasurement,
  id,
}: {
  eachMeasureProperties: MeasureProps;
  eachMeasureKey: string;
  initialMeasure: number;
  idealMeasurement: number;
  id: string;
}) {
  const { addLog } = useContext(LoggingContext);
  const location = useLocation();
  const { userDetailsState } = useContext(UserContext);
  const { unitcm } = userDetailsState;
  const roundHalf = useCallback(
    (unrounded) => {
      const precisionValue = unitcm === 1 ? 2 : 4;
      return unitcm === 2.57
        ? Math.round((unrounded + Number.EPSILON) * precisionValue) / precisionValue
        : Math.round(unrounded);
    },
    [unitcm]
  );

  const idealMeasure = roundHalf(idealMeasurement / unitcm) || 0;
  const units = unitcm === 1 ? 'cm' : '"';

  const [value, setValue] = useState(roundHalf(initialMeasure / unitcm));
  const [differenceAdvisorTextState, setDifferenceAdvisorTextState] = useState('');
  const [isShown, setIsShown] = useState(false);

  const handleSliderChange = (event: Event, newValue: number | number[]) => {
    setValue(newValue as number);
  };

  useEffect(() => {
    setValue(roundHalf(initialMeasure / unitcm));
    setDifferenceAdvisorTextState(() => {
      if (value < 1 || idealMeasure < 1) return '(Comparison not available)';
      if (value < idealMeasure) return '(Fits small)';
      if (value > idealMeasure) return '(Fits large)';
      if (value === idealMeasure) return '(Perfect Fit!)';
      return '';
    });
  }, [value, eachMeasureKey, idealMeasure, initialMeasure, unitcm, roundHalf]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value === '' ? 0 : Number(event.target.value) * unitcm);
  };

  const handleBlur = () => {
    if (value < 0) {
      setValue(0);
    } else if (value > 100) {
      setValue(100);
    }
  };

  function getMeasuresAdvisorText() {
    if (idealMeasure > 0 && initialMeasure > 0)
      return (
        <>
          <span style={{ color: 'darkblue' }}>
            Measures for this garment: {initialMeasure}
            {units}.{' '}
          </span>
          <span style={{ color: 'darkgreen' }}>
            Your ideal length for this measurement is {idealMeasure}
            {units}.
          </span>
        </>
      );
    if (idealMeasure === 0 && initialMeasure > 0)
      return (
        <>
          <span style={{ color: 'darkblue' }}>
            Measures for this garment: {initialMeasure}
            {units}.{' '}
          </span>
          <span>Update your ideal measurement to see a comparison.</span>
        </>
      );
    if (idealMeasure > 0 && initialMeasure === 0)
      return (
        <>
          <span style={{ color: 'black' }}>
            No measurement has been recorded for this garment.{' '}
          </span>
          <span style={{ color: 'darkgreen' }}>
            Your ideal length for this measurement is {idealMeasure}
            {units}.
          </span>
        </>
      );
    return (
      <span style={{ color: 'black' }}>No measurement has been recorded for this garment.</span>
    );
  }

  return (
    <Grid
      item
      xs={12}
      lg={5}
      md={5}
      id={id}
      sx={{
        position: 'relative',
        minWidth: '200px',
        marginBottom: '8px',
      }}
      onMouseEnter={() => {
        addLog('mouse enter (hover) comparison slider',  location.pathname);
        setIsShown(true);
      }}
      onMouseLeave={() => setIsShown(false)}
    >
      <Box // Top Info: Name of Measure and size difference advisor text
        sx={{ display: 'grid', gridTemplateColumns: '80px 1fr' }}
        style={{ marginBottom: -10 }}
      >
        <Typography id='input-slider' gutterBottom style={{ color: 'grey' }}>
          {eachMeasureProperties.name}
        </Typography>
        <Typography
          id='differenceAdvisorTextState'
          gutterBottom
          sx={{
            color: colorDifference(value > 1 && idealMeasure > 1 ? value - idealMeasure : 'nan'),
            opacity: isShown ? 1 : 0,
            transition: 'opacity 0.5s',
            textAlign: 'end',
          }}
        >
          {differenceAdvisorTextState}
        </Typography>
      </Box>
      {/* Image, Slider, Input */}
      <Grid container spacing={2} alignItems='center'>
        <Grid item style={{ opacity: isShown ? 0.3 : 1 }}>
          <CustomIcon imageRef={eachMeasureProperties.imageRef} />
        </Grid>
        <Grid item xs>
          <Slider
            sx={{
              '& .MuiSlider-thumb': {
                height: 2,
                width: 2,
                marginTop: 0,
                marginLeft: 0,
                backgroundColor: 'primary.main',
                border: 'none',
                '&:after': {
                  display: 'none',
                },
              },
              '& .MuiSlider-mark': {
                width: 10,
                height: 10,
                transform: 'translate(-4px, -4px)',
                borderRadius: 5,
                opacity: 1,
                backgroundColor: 'success.main',
              },
              '& .MuiSlider-valueLabel': {
                left: 'auto',
                fontWeight: 600,
                backgroundColor: 'primary.dark',
                borderRadius: '50%',
                height: '20px',
                width: '20px',
                padding: '4px',
              },
              '& .MuiSlider-markLabel': {
                color: 'success.dark',
                fontWeight: 600,
              },
            }}
            value={typeof value === 'number' ? value : 0}
            onChange={handleSliderChange}
            aria-labelledby='input-slider'
            min={Math.min(roundHalf(eachMeasureProperties.min / unitcm), value, idealMeasure)}
            max={Math.max(roundHalf(eachMeasureProperties.max / unitcm), value, idealMeasure)}
            step={unitcm === 1 ? 1 : 0.5}
            valueLabelDisplay={value > 1 ? 'on' : 'off'}
            disabled
            marks={idealMeasure > 0 ? [{ value: idealMeasure, label: `${idealMeasure}` }] : false}
          />
        </Grid>
        <Grid // Input
          item
          sx={{
            width: 52,
            '& .MuiInputBase-input': {
              fontWeight: 550,
              width: 32,
            },
          }}
        >
          <Input
            value={
              value > 1 && idealMeasure > 1
                ? `${value - idealMeasure > 0 ? `+` : ``}${value - idealMeasure}`
                : 'N/A'
            }
            margin='dense'
            onChange={handleInputChange}
            onBlur={handleBlur}
            inputProps={{
              step: 1,
              'aria-labelledby': 'input-slider',
            }}
            disabled
            sx={{
              fontWeight: 300,
              '& .Mui-disabled': {
                textFillColor: colorDifference(
                  value > 1 && idealMeasure > 1 ? value - idealMeasure : 'nan'
                ),
              },
            }}
          />
          <Typography
            id='inchesorcm'
            sx={{
              color: 'grey',
              height: 50,
            }}
          >
            {unitcm === 1 ? 'cm' : 'inches'}
          </Typography>
        </Grid>
      </Grid>
      <Typography // Bottom advisor text
        variant='caption'
        sx={{
          color: 'green',
          transition: 'opacity 0.5s',
          // position: 'absolute',
          right: '3%',
          bottom: '-15%',
          width: '90%',
        }}
        style={{
          opacity: isShown ? 0.8 : 0,
        }}
      >
        {getMeasuresAdvisorText()}
      </Typography>
    </Grid>
  );
}
